import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, TrendingUp, Receipt, Landmark, FileText,
  BarChart3, Tags, Settings, LogOut, Menu, X, CalendarClock, Users, FileSpreadsheet, FileCheck, GraduationCap, FolderKanban, HandCoins, PiggyBank, ClipboardList, Wallet, User,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/income", label: "Income", icon: TrendingUp },
  { to: "/invoices", label: "Invoices", icon: FileSpreadsheet },
  { to: "/quotes", label: "Quotes", icon: FileCheck },
  { to: "/expenses", label: "Expenses", icon: Receipt },
  { to: "/bills", label: "Bills to Pay", icon: ClipboardList },
  { to: "/accounts", label: "Bank Accounts", icon: Wallet },
  { to: "/bank", label: "Bank Feeds", icon: Landmark },
  { to: "/personal", label: "Personal", icon: User },
  { to: "/bas", label: "BAS Statement", icon: FileText },
  { to: "/payroll", label: "Payroll & Super", icon: Users },
  { to: "/pnl", label: "Profit & Loss", icon: BarChart3 },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/loans", label: "Loans", icon: HandCoins },
  { to: "/drawings", label: "Drawings", icon: PiggyBank },
  { to: "/categories", label: "Categories", icon: Tags },
  { to: "/fixed-costs", label: "Fixed Costs", icon: CalendarClock },
  { to: "/settings", label: "Business", icon: Settings },
  { to: "/learn", label: "Learn GST", icon: GraduationCap },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 lg:h-20 flex items-center gap-2.5 border-b border-primary-foreground/10">
        <div className="w-9 h-9 rounded-xl bg-primary-foreground/10 grid place-items-center">
          <span className="text-primary-foreground font-heading font-extrabold text-lg">K</span>
        </div>
        <div className="leading-tight">
          <div className="text-primary-foreground font-heading font-bold text-[15px]">KangarooLedger</div>
          <div className="text-primary-foreground/50 text-[10px] uppercase tracking-widest font-mono">AU GST · BAS</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            className={({ isActive }) =>
              `nav-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                isActive
                  ? "bg-primary-foreground text-primary shadow-sm"
                  : "text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-primary-foreground/10">
        <div className="px-3 py-2 mb-1">
          <div className="text-primary-foreground text-sm font-medium truncate">{user?.name || "Owner"}</div>
          <div className="text-primary-foreground/50 text-xs truncate">{user?.email}</div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="nav-item w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
        >
          <LogOut className="w-[18px] h-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-primary flex-col fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-primary flex flex-col">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="glass-header sticky top-0 z-30 h-16 lg:h-20 border-b border-border flex items-center px-4 sm:px-6 lg:px-8 gap-3">
          <button
            className="lg:hidden p-2 -ml-2 text-foreground"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            GST Registered · Quarterly BAS
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-7xl mx-auto fade-in">{children}</main>
      </div>
    </div>
  );
}
